import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    TextField
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import toast from "react-hot-toast";
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { GlobalStyles, useTheme, useMediaQuery } from '@mui/material';
import { fetchContentReports, fetchContentReportById } from '../../Redux/Reducers/ContentReportsReducer';
import { formatUTCDate, formatUTCDateForDataGrid } from '../../utils/dateUtils';
import { Close } from '@mui/icons-material';
import usePermissions from "../../hooks/usePermissions";
import { CustomLoadingOverlay } from '../../components/common/TableLoader';

const MODULE_KEY = "content-report";

const ContentReportsPage = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { hasAction } = usePermissions();
    const { reports, loading, pagination } = useSelector((state) => state.contentReports || {});
    const [ viewModalOpen, setViewModalOpen ] = useState(false);
    const [ selectedReport, setSelectedReport ] = useState(null);
    const [ searchText, setSearchText ] = useState("");

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    useEffect(() => {
        dispatch(fetchContentReports({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            search: searchText,
        }));
    }, [dispatch, paginationModel, searchText]);

    const contentReportsList = useMemo(() => {
        if (!reports) return [];
        if (Array.isArray(reports)) return reports;
        if (reports.data && Array.isArray(reports.data)) return reports.data;
        return [];
    }, [reports]);

    const handleView = async (rowId = null) => {
        try {
            setSelectedReport(null);
            setViewModalOpen(true);
            if (rowId) {
                const row = await dispatch(fetchContentReportById(rowId)).unwrap();
                setSelectedReport(row);
            }
        } catch (err) {
            toast.error("Failed to fetch audio details");
        }
    };

    const columns = 
    [
        { 
            field: "sno", 
            headerName: t('sno', 'S.No'), 
            width: 80, 
            sortable: false 
        },
        {
            field: "content_type",
            headerName: t('contentReports.fields.content_type', 'Content Type'),
            ...(isMobile && { width: 180 }),
            ...(isLarge && { flex: 1 }),
        },
        {
            field: "content_reason",
            headerName: t('contentReports.fields.content_reason', 'Reason'),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
        },
        {
            field: "created_at",
            headerName: t('contentReports.fields.created_at', 'Created At'),
            ...(isMobile && { width: 180 }),
            ...(isLarge && { flex: 1 }),
            valueFormatter: (value) => {
                return value ? formatUTCDateForDataGrid(value) : "-";
            }
        },
        // { 
        //     field: "message", 
        //     headerName: t('notifications.email_notifications.fields.message', 'Message'), 
        //     ...(isMobile && { width: 150 }),
        //     ...(isLarge && { flex: 1 }),
        //     renderCell: (params) => (
        //         <div
        //             dangerouslySetInnerHTML={{
        //                 __html: params.value,
        //             }}
        //         />
        //     ),
        // },
        {
        field: "actions",
        headerName: t('actions',"Actions"),
        width: 140,
        sortable: false,
        renderCell: (params) => {
            return (
                <Box sx={{ display: 'flex', gap: '6px' }}>
                    <IconButton onClick={() => handleView(params.row.id)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                            <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                            <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
                        </svg>
                    </IconButton>
                </Box>
            )}
        }
    ];
    
    const rows = useMemo(() => {
        return contentReportsList
            .map((record, index) => ({
                id: record.id,
                sno: index + 1,

                content_type: record.content_type || "",

                content_id: record.content_id || "",

                content_title:
                    record.content_title?.lang?.[i18n.language]?.title ||
                    record.content_title?.lang?.en?.title ||
                    "-",

                content_reason:
                    record.content_reason?.lang?.[i18n.language] ||
                    record.content_reason?.lang?.en ||
                    "-",

                created_at: record.created_at || "",
            }))
            .filter((row) => {
                const s = searchText.toLowerCase();

                return (
                    row.content_type.toLowerCase().includes(s) ||
                    row.content_reason.toLowerCase().includes(s)
                );
            });

    }, [contentReportsList, searchText, i18n.language]);


    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('contentReports.title', 'Content Reports')}</Typography>                
                    <TextField
                        size="small"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t('search', 'Search')}
                        sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '999px',
                            backgroundColor: 'rgba(230, 210, 249, 0.2)',
                            color: '#fff',
                            height: '38px',
                            '& fieldset': {
                                borderColor: 'rgba(230, 210, 249, 0.1)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(230, 210, 249, 0.1)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgba(230, 210, 249, 0.1)',
                                borderWidth: '1px',
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '10px 16px',
                            color: '#FF69B4',
                            fontSize: '15px',
                            lineHeight: '18px',
                            
                        },
                        '& input::placeholder': {
                            color: 'rgba(255,255,255,0.6)',
                            opacity: 1,
                        },
                        '& .MuiFormHelperText-root': {
                            ml: '20px'
                        }
                        }}
                    />
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
                        <CardContent sx={{ padding: '0 !important' }}>
                            <Box>
                                <DataGrid
                                    rows={rows}
                                    columns={columns}
                                    loading={loading}
                                    slots={{ toolbar: GridToolbar, loadingOverlay: CustomLoadingOverlay }}
                                    paginationMode="server" 
                                    rowCount={pagination?.total || 0}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={(model) => {
                                        setPaginationModel(model);
                                    }}
                                    slotProps={{
                                        toolbar: {
                                            csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
                                            printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
                                        },
                                    }}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 10 } },
                                        sorting: { sortModel: [{ field: "created_at", sort: "desc" }] }
                                    }}
                                    getRowId={(row) => row.id || row.sno}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    disableRowSelectionOnClick
                                    localeText={i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText}
                                    sx={{ 
                                        backgroundColor: 'transparent', 
                                        border: 'none', 
                                        borderRadius: '0', 
                                        '& .MuiDataGrid-toolbarContainer': {
                                            padding: '12px',
                                            margin: '0 -12px',
                                            gap: '10px',
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                        },
                                        '& .MuiDataGrid-topContainer::after': {
                                            display: 'none',
                                        },
                                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                                            color: '#fff',
                                            border: '1px solid rgba(230, 210, 249, 0.2)',
                                            borderRadius: '999px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            padding: '6px 12px',
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                        },
                                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                                            width: '18px',
                                            height: '18px',
                                        },
                                        '& .MuiDataGrid-virtualScrollerContent': {
                                            maxHeight: 'auto',
                                            height: 'auto !important'
                                        },
                                        '& .MuiDataGrid-virtualScroller': {
                                            borderRadius: '0 !important',
                                        },
                                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                                            position: 'inherit',
                                        },
                                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                                            maxHeight: 'none !important',
                                            height: 'auto !important',
                                        },
                                        '& .MuiDataGrid-cell': {
                                            height: 'auto !important',
                                            minHeight: 'var(--height)',
                                            paddingTop: '5px',
                                            paddingBottom: '5px',
                                        },
                                        '& .MuiDataGrid-cell>.MuiBox-root': {
                                            minHeight: '100%',
                                            alignItems: 'center',
                                        },
                                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                                            borderRadius: '0 !important',
                                            borderColor: 'rgba(230, 210, 249, 0.2) !important',
                                            color: '#fff',
                                        },
                                        '& .MuiDataGrid-columnHeaders [role=row]': {
                                            backgroundColor: '#1a1a1a',
                                        },
                                        '& .MuiDataGrid-footerContainer': {
                                            borderColor: 'rgba(230, 210, 249, 0.2) !important'
                                        },
                                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                                            color: 'white',
                                        },
                                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                                            outline: 'none',
                                        },
                                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                                            opacity: '0.8 !important',
                                            fill: 'white',
                                        },
                                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                                            border: '0 !important',
                                            height: 'auto',
                                            fontWeight: '600',
                                        },
                                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                                            color: "#03c11c",
                                        },
                                        '& .MuiTablePagination-root': {
                                            color: '#fff'
                                        },
                                        '& .MuiTablePagination-selectLabel': {
                                            marginBottom: '0',
                                        },
                                        '& .MuiTablePagination-input': {
                                            marginRight: '10px',
                                        },
                                        '& .MuiTablePagination-input .MuiSelect-select': {
                                            paddingTop: '2px',
                                            paddingBottom: '2px',
                                            border: '1px solid rgba(230, 210, 249, 0.2)',
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            borderRadius: '4px',
                                        },
                                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                                            fill: 'white'
                                        },
                                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                                            marginBottom: '0',
                                        },
                                        '& .MuiTablePagination-actions': {
                                            marginLeft: '10px !important',
                                        },
                                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                                            color: '#FF69B4',
                                        },
                                        '& .MuiDataGrid-filler': {
                                            height: 'auto !important',
                                        },
                                        '& .MuiDataGrid-filler>div': {
                                            borderColor: 'transparent',
                                        },
                                        '& .MuiDataGrid-overlayWrapper': {
                                            height: 'auto !important',
                                            maxHeight: '50px',
                                        },
                                        '& .MuiDataGrid-overlayWrapperInner': {
                                            minHeight: '40px',
                                            height: 'auto !important'
                                        },
                                        '& .MuiDataGrid-overlay': {
                                            color: '#fff',
                                            borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                                            backgroundColor: 'transparent',
                                            minHeight: '40px',
                                        },
                                        // '& .MuiDataGrid-row .MuiDataGrid-cellOffsetLeft + .MuiDataGrid-cell + .MuiDataGrid-cell': {
                                        //     display: 'flex',
                                        //     alignItems: 'center',
                                        //     justifyContent: 'center', 
                                        // },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* View Album Dialog */}
            <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
                    {t('contentReports.popup_titles.view',"View Content Report")}
                    <IconButton onClick={() => setViewModalOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
                    <Close />
                    </IconButton>
                </DialogTitle>
    
                <DialogContent 
                    dividers 
                    sx={{ 
                    borderColor: 'rgba(230, 210, 249, 0.1)', 
                    padding: '24px 16px',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(230, 210, 249, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                        background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                    }}
                >
                    {selectedReport ? (
                    <>
                        <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                            <strong>{t('contentReports.fields.content_type', 'Content Type')}:</strong>{" "}
                        {
                            selectedReport?.content_type || "-"
                        }
                        </Typography>

                        <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                            <strong>{t('contentReports.fields.content_reason', 'Content Reason')}:</strong>{" "}
                        {
                            selectedReport?.content_reason?.lang?.[i18n.language] ||
                            selectedReport?.content_reason?.lang?.en ||
                            "-"
                        }
                        </Typography>

                        <Typography
                            variant="body2"
                            gutterBottom
                            sx={{ fontSize: '14px', mb: '5px' }}
                        >
                            <strong>{t('contentReports.fields.content_title', 'Content Title')}:</strong>{" "}
                            {
                                selectedReport?.content_title?.lang?.[i18n.language]?.title ||
                                selectedReport?.content_title?.lang?.en?.title ||
                                "-"
                            }
                        </Typography>
        
                        <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                        {t('contentReport.fields.created_at',"Created At")}: { formatUTCDateForDataGrid(selectedReport.created_at) }
                        </Typography>
                    </>
                    ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" p={4} minHeight={200}>
                        <CircularProgress sx={{ color: '#FF69B4' }} />
                    </Box>
                    )}
                </DialogContent>
    
                <DialogActions sx={{ px: '16px', py: '20px',flexWrap: 'wrap', gap: '6px' }}>
                    <Button onClick={() => setViewModalOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('close','Close')}</Button>
                </DialogActions>
            </Dialog>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                }}
            />
        </Box>
    );
};

export default ContentReportsPage;


